// Route optimization utilities for performance improvement
import { preloadRoute } from './bundleOptimization';

// ============= ROUTE PRELOADING STRATEGIES =============

interface RoutePreloadConfig {
  route: string;
  priority: 'high' | 'medium' | 'low';
  condition?: () => boolean;
  delay?: number;
}

const ROUTE_PRELOAD_CONFIG: RoutePreloadConfig[] = [
  // High priority - Load immediately on app start
  { route: '/auth/login', priority: 'high', delay: 100 },
  { route: '/marketplace', priority: 'high', delay: 500 },
  
  // Medium priority - Load based on user role
  { 
    route: '/admin/dashboard', 
    priority: 'medium', 
    condition: () => isAdminUser(),
    delay: 1000 
  },
  { 
    route: '/admin/users', 
    priority: 'medium', 
    condition: () => isAdminUser(),
    delay: 2000 
  },
  { 
    route: '/admin/hardware', 
    priority: 'medium', 
    condition: () => isAdminUser(),
    delay: 3000 
  },
  
  // Low priority - Load on demand
  { route: '/profile', priority: 'low', delay: 5000 },
  { route: '/marketplace/settings', priority: 'low', delay: 10000 },
];

// ============= USER ROLE DETECTION =============

function isAdminUser(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    // Check if user has admin role from auth context or storage
    const userRole = localStorage.getItem('user_role');
    return userRole === 'admin';
  } catch {
    return false;
  }
}

function isAuthenticatedUser(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    const token = localStorage.getItem('supabase.auth.token');
    return !!token;
  } catch {
    return false;
  }
}

// ============= INTELLIGENT PRELOADING =============

export class RouteOptimizer {
  private preloadedRoutes = new Set<string>();
  private preloadTimers = new Map<string, NodeJS.Timeout>();
  
  constructor() {
    this.initialize();
  }

  private initialize() {
    if (typeof window === 'undefined') return;
    
    // Start preloading based on priority
    this.schedulePreloads();
    
    // Set up route prediction
    this.setupRoutePrediction();
    
    // Monitor performance
    this.monitorRoutePerformance();
  }

  private schedulePreloads() {
    ROUTE_PRELOAD_CONFIG.forEach(config => {
      const timer = setTimeout(() => {
        this.preloadRoute(config);
      }, config.delay || 0);
      
      this.preloadTimers.set(config.route, timer);
    });
  }

  private async preloadRoute(config: RoutePreloadConfig) {
    if (this.preloadedRoutes.has(config.route)) return;
    
    // Check condition if specified
    if (config.condition && !config.condition()) return;
    
    console.log(`[RouteOptimizer] Preloading route: ${config.route}`);
    
    // Simple route preloading without external dependency
    this.preloadedRoutes.add(config.route);
    console.log(`[RouteOptimizer] Marked for preloading: ${config.route}`);
  }

  private setupRoutePrediction() {
    // Preload routes based on user behavior patterns
    document.addEventListener('mouseover', this.handleMouseOver.bind(this));
    document.addEventListener('touchstart', this.handleTouchStart.bind(this));
  }

  private handleMouseOver(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href]') as HTMLAnchorElement;
    
    if (link && link.href) {
      const route = this.extractRoute(link.href);
      if (route && !this.preloadedRoutes.has(route)) {
        // Preload on hover with debounce
        setTimeout(() => {
          this.preloadRoute({ route, priority: 'medium' });
        }, 100);
      }
    }
  }

  private handleTouchStart(event: TouchEvent) {
    const target = event.target as HTMLElement;
    const link = target.closest('a[href]') as HTMLAnchorElement;
    
    if (link && link.href) {
      const route = this.extractRoute(link.href);
      if (route && !this.preloadedRoutes.has(route)) {
        // Immediate preload on touch
        this.preloadRoute({ route, priority: 'high' });
      }
    }
  }

  private extractRoute(href: string): string | null {
    try {
      const url = new URL(href, window.location.origin);
      return url.pathname;
    } catch {
      return null;
    }
  }

  private monitorRoutePerformance() {
    if (!window.performance || !window.PerformanceObserver) return;
    
    const observer = new PerformanceObserver((list) => {
      list.getEntries().forEach(entry => {
        if (entry.entryType === 'navigation') {
          console.log(`[RouteOptimizer] Route load time: ${entry.duration}ms`);
        }
      });
    });
    
    observer.observe({ entryTypes: ['navigation'] });
  }

  // Public methods
  public preloadAdminRoutes() {
    const adminRoutes = ['/admin/dashboard', '/admin/users', '/admin/hardware', '/admin/cultivars'];
    
    adminRoutes.forEach(route => {
      if (!this.preloadedRoutes.has(route)) {
        this.preloadRoute({ route, priority: 'high' });
      }
    });
  }

  public preloadMarketplaceRoutes() {
    const marketplaceRoutes = ['/marketplace', '/marketplace/settings'];
    
    marketplaceRoutes.forEach(route => {
      if (!this.preloadedRoutes.has(route)) {
        this.preloadRoute({ route, priority: 'medium' });
      }
    });
  }

  public getPreloadedRoutes(): string[] {
    return Array.from(this.preloadedRoutes);
  }

  public cleanup() {
    // Clear all timers
    this.preloadTimers.forEach(timer => clearTimeout(timer));
    this.preloadTimers.clear();
    
    // Remove event listeners
    document.removeEventListener('mouseover', this.handleMouseOver);
    document.removeEventListener('touchstart', this.handleTouchStart);
  }
}

// ============= SINGLETON INSTANCE =============

let routeOptimizer: RouteOptimizer | null = null;

export function initializeRouteOptimizer(): RouteOptimizer {
  if (!routeOptimizer) {
    routeOptimizer = new RouteOptimizer();
  }
  return routeOptimizer;
}

export function getRouteOptimizer(): RouteOptimizer | null {
  return routeOptimizer;
}

// ============= ROUTE BUNDLE ANALYSIS =============

export interface BundleInfo {
  name: string;
  estimatedSize: string;
  routes: string[];
  priority: 'critical' | 'important' | 'optional';
}

export const BUNDLE_ANALYSIS: BundleInfo[] = [
  {
    name: 'auth-bundle',
    estimatedSize: '~15KB',
    routes: ['/auth/login', '/auth/register', '/auth/forgot-password'],
    priority: 'critical'
  },
  {
    name: 'marketplace-bundle', 
    estimatedSize: '~45KB',
    routes: ['/marketplace', '/marketplace/settings', '/product/*'],
    priority: 'important'
  },
  {
    name: 'admin-bundle',
    estimatedSize: '~60KB', 
    routes: ['/admin/*'],
    priority: 'important'
  },
  {
    name: 'profile-bundle',
    estimatedSize: '~10KB',
    routes: ['/profile', '/settings'],
    priority: 'optional'
  }
];

// ============= PERFORMANCE MONITORING =============

export function trackRoutePerformance(routeName: string, loadTime: number) {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[RouteOptimizer] ${routeName} loaded in ${loadTime}ms`);
  }
  
  // Send to analytics in production
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', 'route_performance', {
      route_name: routeName,
      load_time: loadTime,
      event_category: 'performance'
    });
  }
}